<template>
  <div class="agent-models-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Agent Models
        </h1>
        <div class="header-actions d-flex align-center gap-3 flex-wrap">
          <v-btn
            color="primary"
            class="mank-btn"
            elevation="0"
            :to="{ name: 'CreateAgentModel' }">
            <Icon name="plus" variant="solid" color="onPrimary"/>
            Create Agent Model
          </v-btn>
        </div>
      </div>

      <div class="main-content">
        <div v-if="loading" class="loading-state py-12 text-center">
          <v-progress-circular indeterminate color="primary" size="40"/>
          <p class="loading-text mt-4 text-body-2 text--secondary">
            Loading Agent Models…
          </p>
        </div>

        <div v-else-if="models.length === 0" class="empty-state py-12 text-center">
          <v-icon size="48" color="primary" class="mb-4">
            mdi-brain
          </v-icon>
          <h2 class="text-h6 text-bold mb-2">
            No Agent Models
          </h2>
          <p class="text-body-2 text--secondary mb-6">
            No Agent Models yet. Create one with an external path and typed parameters.
          </p>
          <v-btn
            color="primary"
            class="mank-btn"
            elevation="0"
            :to="{ name: 'CreateAgentModel' }">
            <Icon name="plus" variant="solid" color="onPrimary"/>
            Create Agent Model
          </v-btn>
        </div>

        <div v-else class="models-grid">
          <div
            v-for="item in models"
            :key="item.id"
            class="model-card">
            <div class="card-top">
              <div>
                <h3 class="model-name text-element-2 text-bold">
                  {{ displayName(item) }}
                </h3>
                <span class="source-pill">{{ displaySource(item) }}</span>
              </div>
              <div class="card-actions d-flex gap-1">
                <v-btn
                  icon
                  small
                  class="mank-btn-icon"
                  :to="{ name: 'EditAgentModel', params: { id: item.id } }">
                  <Icon name="arrow-right" variant="solid" size="16" color="default"/>
                </v-btn>
                <v-btn
                  icon
                  small
                  class="mank-btn-icon"
                  @click="openDelete(item)">
                  <Icon name="trash" variant="solid" size="16" color="default"/>
                </v-btn>
              </div>
            </div>
            <p class="model-desc text-body-2 text--secondary">
              {{ displayDescription(item) || '—' }}
            </p>
            <div v-if="displayEndpoint(item)" class="endpoint text-caption font-mono text--secondary">
              {{ displayEndpoint(item) }}
            </div>
            <div v-if="paramCount(item)" class="params-hint text-caption text--secondary mt-2">
              {{ paramCount(item) }} parameter(s)
            </div>
            <div class="id-row text-caption mt-3 d-flex align-center">
              <span class="id-text text-truncate">{{ item.id }}</span>
              <v-btn icon x-small class="ml-1" @click="copyId(item.id)">
                <v-icon size="14">
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :visible="showDelete"
      @cancel="showDelete = false"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import AgentService from '@/services/AgentService';

export default {
  name: 'AgentModels',
  components: { Icon, DeleteConfirmationModal },
  data() {
    return {
      loading: false,
      models: [],
      showDelete: false,
      deleteTarget: null,
    };
  },
  mounted() {
    this.fetchModels();
  },
  methods: {
    displayName(m) {
      return m.name ?? m.Name ?? '';
    },
    displayDescription(m) {
      return m.description ?? m.Description ?? '';
    },
    displaySource(m) {
      return m.source ?? m.Source ?? '';
    },
    displayEndpoint(m) {
      return m.external_path ?? m.ExternalPath ?? m.endpoint_url ?? m.EndpointURL ?? '';
    },
    paramCount(m) {
      const p = m.params ?? m.Params;
      return Array.isArray(p) ? p.length : 0;
    },
    async fetchModels() {
      this.loading = true;
      this.models = [];
      try {
        const res = await AgentService.getAgentModels({ source: 'TTS' });
        if (res?.success && Array.isArray(res.data)) {
          this.models = res.data;
        }
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Failed to load agent models.',
        }, { root: true });
      } finally {
        this.loading = false;
      }
    },
    copyId(id) {
      if (!navigator.clipboard || !navigator.clipboard.writeText) return;
      navigator.clipboard.writeText(id).then(() => {
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: 'ID copied.',
        }, { root: true });
      });
    },
    openDelete(item) {
      this.deleteTarget = item;
      this.showDelete = true;
    },
    async confirmDelete() {
      if (!this.deleteTarget?.id) {
        this.showDelete = false;
        return;
      }
      try {
        const res = await AgentService.deleteAgentModel(this.deleteTarget.id);
        if (res?.success !== false) {
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            message: 'Agent Model deleted.',
          }, { root: true });
          await this.fetchModels();
        }
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Could not delete Agent Model.',
        }, { root: true });
      } finally {
        this.showDelete = false;
        this.deleteTarget = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/colors.scss';

.main-content {
  padding: 24px;
  overflow-y: auto;
}

.models-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.model-card {
  background: $light-surface-2;
  border: 1px solid $light-border;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 180px;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.model-name {
  font-size: $body-2-size;
  line-height: 1.3;
  margin-bottom: 6px;
}

.source-pill {
  display: inline-block;
  font-size: $text-size-11;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(71, 137, 247, 0.12);
  color: #4789f7;
}

.model-desc {
  flex: 1;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.endpoint {
  word-break: break-all;
  margin-top: 8px;
}

.id-row {
  border-top: 1px solid $light-border;
  padding-top: 8px;
  .id-text {
    max-width: 100%;
    font-family: monospace;
    font-size: $text-size-11;
  }
}

.font-mono {
  font-family: monospace;
  font-size: $caption-1-size;
}
</style>
